import { IRequestService } from '../iRequestService';
import { ServiceRequest, Route, Invoice, Particular, Language, DraftTransportRequest, OrgLocation, RequestStatus } from '@/types/entity';
import {
    QueryRecordsRequest, ApiRequest, GetServiceRequestsInput, CancelRequest,
    EditParticipantRequest, EditSpecificDetailsRequest,
} from '@/types/request';
import { ObjectFactory } from '@/config/objectFactory';
import { MedTransGoConstants } from '@/util/constants';
import { ApiResponse, ServiceRequestStatistics } from '@/types/response';
import { DateUtils } from '@/util/dateUtils';
import { CommonUtils } from '@/util/commonUtils';
import { EventBus } from '@/main';
import { ToastEvent } from '@/types/event';
import { ResourceUtils } from '@/util/resourceUtils';
import { Condition } from '@/types/queryRequest';


export class RequestService implements IRequestService {

    public async getRequests(getServiceRequestsInput: GetServiceRequestsInput, loaderEvent?: boolean):
        Promise<ApiResponse<ServiceRequest[]>> {
        const queryRecordsRequest: QueryRecordsRequest = {
            conditions: getServiceRequestsInput.conditions ? getServiceRequestsInput.conditions : [],
            order: {
                createdOn: -1,
            },
            limit: getServiceRequestsInput.limit || 10,
            start: getServiceRequestsInput.start,
            responseType: getServiceRequestsInput.responseType,
        };
        if (getServiceRequestsInput.service && queryRecordsRequest.conditions) {
            queryRecordsRequest.conditions.push({
                op: 'ne',
                fieldName: getServiceRequestsInput.service,
                value: null,
            });
        }
        if (getServiceRequestsInput.advanceSearchRequest && queryRecordsRequest.conditions) {
            const advancedSearchRequest = getServiceRequestsInput.advanceSearchRequest;
            if (advancedSearchRequest.appointmentDate) {
                if (advancedSearchRequest.appointmentDate.startDate) {
                    const startDate = DateUtils.convertToUTCInMillis(advancedSearchRequest.appointmentDate.startDate);
                    queryRecordsRequest.conditions.push({
                        fieldName: 'appointment.timestamp',
                        op: 'gte',
                        value: startDate,
                    });
                }
                if (advancedSearchRequest.appointmentDate.endDate) {
                    const endDate = DateUtils.convertToUTCInMillis(advancedSearchRequest.appointmentDate.endDate);
                    queryRecordsRequest.conditions.push({
                        fieldName: 'appointment.timestamp',
                        op: 'lte',
                        value: endDate,
                    });
                }
                if (advancedSearchRequest.status && advancedSearchRequest.status.length > 0) {
                    if (getServiceRequestsInput.service === 'transportRequest') {
                        queryRecordsRequest.conditions = this.getConditionForStatus(advancedSearchRequest.status,
                            queryRecordsRequest.conditions);
                    } else {
                        queryRecordsRequest.conditions.push({
                            fieldName: 'status',
                            op: 'in',
                            value: advancedSearchRequest.status,
                        });
                    }
                }
                if (advancedSearchRequest.searchString) {
                    queryRecordsRequest.conditions.push({
                        conditions: [{
                            fieldName: 'requestId',
                            op: 'eq',
                            value: advancedSearchRequest.searchString,
                        },
                        {
                            fieldName: 'appointment.patient.fullName',
                            op: 'regex',
                            value: advancedSearchRequest.searchString,
                        },
                        {
                            fieldName: 'appointment.facility.name',
                            op: 'regex',
                            value: advancedSearchRequest.searchString,
                        }],
                        op: 'or',
                        fieldName: '',
                        value: undefined,
                    });
                }
            }
        }
        if (getServiceRequestsInput.status && queryRecordsRequest.conditions) {
            queryRecordsRequest.conditions.push({
                op: 'eq',
                fieldName: 'status',
                value: getServiceRequestsInput.status,
            });
        }
        const request: ApiRequest = {
            baseURL: getServiceRequestsInput.baseURL,
            url: `${MedTransGoConstants.HTTP_URL_REQUEST}/list`,
            data: queryRecordsRequest,
        };
        if (loaderEvent === undefined) {
            request.loaderEvent = {
                show: true,
            };
        }
        if (getServiceRequestsInput.responseType) {
            request.responseType = 'arraybuffer';
        }
        const response = await ObjectFactory.getHttpService().post<ServiceRequest[]>(request);
        if (!response.success) {
            CommonUtils.showError(response);
        }
        if (getServiceRequestsInput.responseType) {
            if (getServiceRequestsInput.responseType === 'excelReport') {
                const file = new Blob([response as any],
                    { type: 'vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                const fileName = `ServiceReport_${DateUtils.formatDate(Date.now(), 'MMDDYYYYHHMM')}.xlsx`;
                ResourceUtils.downloadFileIntoDevice(file, fileName);
                return response;
            } else {
                const file = new Blob([response as any], { type: 'application/pdf' });
                ResourceUtils.downloadFileIntoDevice(file,
                    `ServiceReport_${DateUtils.formatDate(Date.now(), 'MMDDYYYYHHMM')}.pdf`);
                return response;
            }
        } else {
            await this.getFormattedRequests(response.data as any, getServiceRequestsInput.baseURL);
            return response;
        }
    }

    public async approveRequest(requestId: string, baseURL: string) {
        const apiRequest: ApiRequest = {
            baseURL,
            url: `${MedTransGoConstants.HTTP_URL_REQUEST}/${requestId}/approve`,
            loaderEvent: {
                show: true,
            },
        };
        return await ObjectFactory.getHttpService().post<ServiceRequest>(apiRequest);
    }

    public async getRequestDetails(requestId: string, baseURL: string,
                                   loaderEvent?: boolean): Promise<ApiResponse<any>> {
        const apiRequest: ApiRequest = {
            baseURL,
            url: `${MedTransGoConstants.HTTP_URL_REQUEST}/${requestId}`,
        };
        if (loaderEvent) {
            apiRequest.loaderEvent = {
                show: true,
            };
        }
        const response = await ObjectFactory.getHttpService().get<ServiceRequest>(apiRequest);
        if (response.success && response.data) {
            const serviceRequest = response.data;
            if (serviceRequest.transportRequest && serviceRequest.interpreterRequest) {
                serviceRequest.serviceType = 'Transportation & Interpretation';
            } else if (serviceRequest.interpreterRequest && serviceRequest.telemedicineRequest) {
                serviceRequest.serviceType = `Telemedicine & Interpretation (${serviceRequest.interpreterRequest.serviceType})`;
            } else if (serviceRequest.telemedicineRequest) {
                serviceRequest.serviceType = 'Telemedicine';
            } else if (serviceRequest.transportRequest) {
                serviceRequest.serviceType = 'Transportation';
            } else if (serviceRequest.interpreterRequest) {
                serviceRequest.serviceType = `Interpretation (${serviceRequest.interpreterRequest.serviceType})`;
            } else if (serviceRequest.deliveryRequest) {
                serviceRequest.serviceType = 'Medication Delivery';
            }

            if (serviceRequest.transportRequest) {
                serviceRequest.transportRequest.mapRoutes =
                    await this.getRoutes(serviceRequest.transportRequest.routes, baseURL, loaderEvent);
                serviceRequest.transportRequest.totalDistance = 0;
                serviceRequest.transportRequest.mapRoutes.forEach((element) => {
                    if (serviceRequest.transportRequest && element.totalMiles) {
                        serviceRequest.transportRequest.totalDistance += element.totalMiles;
                    }
                });
            }
            if (serviceRequest.deliveryRequest) {
                serviceRequest.deliveryRequest.mapRoutes =
                    await this.getRoutes(serviceRequest.deliveryRequest.routes, baseURL, loaderEvent);
            }

            if (serviceRequest.payment && !serviceRequest.payment.invoiceId) {
                if (serviceRequest.payment.estimatedFare) {
                    serviceRequest.payment.estimatedFare.particulars =
                        this.groupParticulars(serviceRequest.payment.estimatedFare.particulars);
                }
            }
        }
        return response;
    }

    public async getRequestStatistics(baseURL: string):
        Promise<ApiResponse<ServiceRequestStatistics>> {
        const iHttpService = ObjectFactory.getHttpService();
        const apiRequest: ApiRequest = {
            baseURL,
            url: `${MedTransGoConstants.HTTP_URL_REQUEST}/statistics`,
            loaderEvent: {
                show: true,
            },
        };
        const response = await iHttpService.get<ServiceRequestStatistics>(apiRequest);
        if (!response.success) {
            CommonUtils.showError(response);
        }
        return response;
    }

    public async getRequestInvoice(invoiceId: string, baseURL: string, loaderEvent?: boolean):
        Promise<ApiResponse<Invoice>> {
        const iHttpService = ObjectFactory.getHttpService();
        const apiRequest: ApiRequest = {
            baseURL,
            url: `${MedTransGoConstants.HTTP_URL_INVOICES}/${invoiceId}`,
        };
        if (loaderEvent) {
            apiRequest.loaderEvent = {
                show: true,
            };
        }
        const response = await iHttpService.get<Invoice>(apiRequest);
        if (response.success && response.data) {
            response.data.totalAmount = 0;
            response.data.particulars = this.groupParticulars(response.data.particulars);
            response.data.particulars.forEach((element: Particular) => {
                if (response.data) {
                    response.data.totalAmount += element.amount;
                }
            });
        } else {
            CommonUtils.showError(response);
        }
        return response;
    }

    public async cancelRequest(requestId: string, cancelRequest: CancelRequest, baseURL: string):
        Promise<ApiResponse<any>> {
        const iHttpService = ObjectFactory.getHttpService();
        const apiRequest: ApiRequest = {
            baseURL,
            url: `${MedTransGoConstants.HTTP_URL_REQUEST}/${requestId}`,
            data: cancelRequest,
            loaderEvent: {
                show: true,
            },
        };
        const response = await iHttpService.delete<ServiceRequest>(apiRequest);
        if (response.success) {
            const toastEvent: ToastEvent = {
                message: 'Request has been successfully canceled',
                title: 'Success',
                varient: 'success',
                timeout: 2000,
            };
            EventBus.$emit(MedTransGoConstants.EVENT_SHOW_TOAST, toastEvent);
        } else {
            CommonUtils.showError(response);
        }
        return response;
    }


    public async editRequest(requestId: string, editParticipantRequest: EditParticipantRequest, baseURL: string):
        Promise<ApiResponse<any>> {
        const iHttpService = ObjectFactory.getHttpService();
        const apiRequest: ApiRequest = {
            baseURL,
            url: `${MedTransGoConstants.HTTP_URL_REQUEST}/${requestId}/participants`,
            data: editParticipantRequest,
            loaderEvent: {
                show: true,
            },
        };
        const response = await iHttpService.put<ServiceRequest>(apiRequest);
        if (response.success) {
            const toastEvent: ToastEvent = {
                message: 'Request has been successfully updated',
                title: 'Success',
                varient: 'success',
                timeout: 2000,
            };
            EventBus.$emit(MedTransGoConstants.EVENT_SHOW_TOAST, toastEvent);
        } else {
            CommonUtils.showError(response);
        }
        return response;
    }

    public async editServiceRequestDetails(requestId: string, editDetailsRequest: EditSpecificDetailsRequest,
                                           baseURL: string): Promise<ApiResponse<any>> {
        const iHttpService = ObjectFactory.getHttpService();
        const apiRequest: ApiRequest = {
            baseURL,
            url: `${MedTransGoConstants.HTTP_URL_REQUEST}/${requestId}/updaterequest`,
            data: editDetailsRequest,
            loaderEvent: {
                show: true,
            },
        };
        const response = await iHttpService.put<ServiceRequest>(apiRequest);
        if (response.success) {
            const toastEvent: ToastEvent = {
                message: 'Request has been successfully updated',
                title: 'Success',
                varient: 'success',
                timeout: 2000,
            };
            EventBus.$emit(MedTransGoConstants.EVENT_SHOW_TOAST, toastEvent);
        } else {
            CommonUtils.showError(response);
        }
        return response;
    }


    public async getLanguages(serviceType: number, baseURL: string): Promise<ApiResponse<Language[]>> {
        const iHttpService = ObjectFactory.getHttpService();
        const apiRequest: ApiRequest = {
            baseURL,
            url: `${MedTransGoConstants.HTTP_URL_LANGUAGES}`,
            loaderEvent: {
                show: true,
            },
        };
        const response = await iHttpService.get<Language[]>(apiRequest);
        return response;
    }

    public async changeInterpreter(requestId: string, baseURL: string): Promise<ApiResponse<any>> {
        const iHttpService = ObjectFactory.getHttpService();
        const apiRequest: ApiRequest = {
            baseURL,
            url: `${MedTransGoConstants.HTTP_URL_REQUEST}/${requestId}/changeInterpreter`,
            loaderEvent: {
                show: true,
            },
        };
        const response = await iHttpService.put<string>(apiRequest);
        if (response.success) {
            const toastEvent: ToastEvent = {
                message: 'Change interpreter request has been sent successfully',
                title: 'Success',
                varient: 'success',
                timeout: 5000,
            };
            EventBus.$emit(MedTransGoConstants.EVENT_SHOW_TOAST, toastEvent);
        } else {
            CommonUtils.showError(response);
        }
        return response;
    }

    public async getDraftRequestDetails(requestId: string, baseURL: string):
        Promise<ApiResponse<{ draftRequest: DraftTransportRequest; billingLocations: OrgLocation[] }>> {
        const iHttpService = ObjectFactory.getHttpService();
        const apiRequest: ApiRequest = {
            baseURL,
            url: `${MedTransGoConstants.HTTP_URL_REQUESTS_V2}/draft/${requestId}/transportation`,
            loaderEvent: {
                show: true,
            },
        };
        const response = await iHttpService.get<{
            draftRequest: DraftTransportRequest;
            billingLocations: OrgLocation[]
        }>
            (apiRequest);
        if (!response.success) {
            CommonUtils.showError(response);
        }
        return response;
    }

    public async submitDraftRequestDetails(requestId: string, baseURL: string, draftTransportRequest:
        DraftTransportRequest):
        Promise<ApiResponse<string>> {
        const iHttpService = ObjectFactory.getHttpService();
        const apiRequest: ApiRequest = {
            baseURL,
            url: `${MedTransGoConstants.HTTP_URL_REQUESTS_V2}/draft/${requestId}/transportation`,
            loaderEvent: {
                show: true,
            },
            data: draftTransportRequest,
        };
        const response = await iHttpService.put<string>(apiRequest);
        if (!response.success) {
            CommonUtils.showError(response);
        }
        return response;
    }

    private async getFormattedRequests(result: ServiceRequest[], baseURL: string) {
        result.forEach(async (element: ServiceRequest) => {
            element.appointment.date = DateUtils.formatDate(
                element.appointment.date,
                DateUtils.FORMAT_DATETIME_AM_PM,
            ) as any;
            element.createdOn = DateUtils.formatDate(
                element.createdOn,
                DateUtils.FORMAT_DATETIME_AM_PM,
            ) as any;
            if (element.interpreterRequest) {
                const hours = element.interpreterRequest.estimatedTime.hours;
                const minutes = element.interpreterRequest.estimatedTime.minutes;
                element.interpreterRequest.estimatedTime = `${hours}:${minutes} Hrs` as any;
            }
        });
    }

    private async getRoutes(routes: Route[], baseURL: string, loaderEvent?: boolean): Promise<Route[]> {
        return new Promise<Route[]>((resolve, reject) => {
            if (routes) {
                const mapRoutes: Route[] = [];
                const promises: Array<Promise<ApiResponse<Route>>> = [];
                routes.forEach((element) => {
                    const routeId = element.routeId;
                    const apiRequest: ApiRequest = {
                        baseURL,
                        url: `${MedTransGoConstants.HTTP_URL_ROUTES}/${routeId}`,
                    };
                    if (loaderEvent) {
                        apiRequest.loaderEvent = {
                            show: true,
                        };
                    }
                    const iHttpService = ObjectFactory.getHttpService();
                    const requestPromise = iHttpService.get<Route>(apiRequest);
                    promises.push(requestPromise);
                });
                Promise.all(promises).then((responses) => {
                    let index = 0;
                    responses.forEach((response) => {
                        if (response.success && response.data) {
                            const route = response.data;
                            const origin = {
                                lat: route.pickupAddress.latitude,
                                lng: route.pickupAddress.longitude, icon: 'ambulance-icon.png',
                            };
                            const destination = {
                                lat: route.dropoffAddress.latitude,
                                lng: route.dropoffAddress.longitude, icon: 'hospital-icon.png',
                            };
                            const stop = {
                                lat: route.dropoffAddress.latitude,
                                lng: route.dropoffAddress.longitude, icon: 'pharmacy-icon.png',
                            };
                            const googleMapRoute: any = {
                                destination,
                                markers: [],
                                origin,
                                stop,
                            };
                            googleMapRoute.markers.push({ position: origin });
                            if (route.additionalStops && route.additionalStops.length > 0) {
                                googleMapRoute.additionalStops = route.additionalStops;
                                route.additionalStops.forEach((element) => {
                                    const rxStop = {
                                        lat: element.address.latitude,
                                        lng: element.address.longitude,
                                        icon: 'pharmacy-icon.png',
                                    };
                                    googleMapRoute.markers.push({ position: rxStop });
                                });
                            }
                            googleMapRoute.markers.push({ position: destination });
                            if (index++ > 1) {
                                destination.icon = 'home-icon.png';
                            }
                            route.googleMap = googleMapRoute;
                            mapRoutes.push(route);
                        } else {
                            CommonUtils.showError(response);
                        }
                    });
                    resolve(mapRoutes);
                }).catch(() => {
                    reject('Failed to get route');
                });
            }
        });
    }

    private groupParticulars(particulars: Particular[]): Particular[] {
        const map = new Map<string, Particular>();
        particulars.forEach((particular: Particular) => {
            if (map.has(particular.description)) {
                const existingParticular = map.get(particular.description);
                if (existingParticular) {
                    existingParticular.amount += particular.amount;
                    existingParticular.quantity += particular.quantity;
                }
            } else {
                map.set(particular.description, particular);
            }
        });
        return Array.from(map.values());
    }

    private getConditionForStatus(status: RequestStatus[], conditions: Condition[]) {
        if (status.includes('Pending') && !status.includes('Confirmed')) {
            conditions.push(
                {
                    conditions: [{
                        conditions: [{
                            fieldName: 'status',
                            op: 'eq',
                            value: 'Pending',
                        },
                        {
                            op: 'eq',
                            fieldName: 'transportRequest.rideshareService',
                            value: false,
                        }],
                        op: 'and',
                    },
                    status.includes('Completed') ? {
                        conditions: [{
                            fieldName: 'status',
                            op: 'eq',
                            value: 'Completed',
                        }],
                        op: 'and',
                    } : status.includes('Canceled') ? {
                        conditions: [{
                            fieldName: 'status',
                            op: 'eq',
                            value: 'Canceled',
                        }],
                        op: 'and',
                    } : {} as any],
                    op: 'or',
                },
            );
        } else if (status.includes('Confirmed') && !status.includes('Pending')) {
            conditions.push({
                conditions: [
                    {
                        fieldName: 'status',
                        op: 'eq',
                        value: 'Confirmed',
                    },
                    {
                        conditions: [{
                            op: 'eq',
                            fieldName: 'status',
                            value: 'Pending',
                        },
                        {
                            op: 'eq',
                            fieldName: 'transportRequest.rideshareService',
                            value: true,
                        }],
                        op: 'and',
                    },
                    status.includes('Completed') ? {
                        conditions: [{
                            fieldName: 'status',
                            op: 'eq',
                            value: 'Completed',
                        }],
                        op: 'and',
                    } : status.includes('Canceled') ? {
                        conditions: [{
                            fieldName: 'status',
                            op: 'eq',
                            value: 'Canceled',
                        }],
                        op: 'and',
                    } : {} as any,
                ],
                op: 'or',
            });
        } else if (status.includes('Pending') && status.includes('Confirmed')) {
            conditions.push({
                conditions: [{
                    op: 'in',
                    fieldName: 'status',
                    value: ['Confirmed', 'Pending'],
                },
                {
                    conditions: [{
                        op: 'eq',
                        fieldName: 'status',
                        value: 'Pending',
                    },
                    {
                        op: 'eq',
                        fieldName: 'transportRequest.rideshareService',
                        value: false,
                    }],
                    op: 'and',
                },
                {
                    conditions: [{
                        op: 'eq',
                        fieldName: 'status',
                        value: 'Confirmed',
                    },
                    {
                        op: 'eq',
                        fieldName: 'transportRequest.rideshareService',
                        value: true,
                    }],
                    op: 'and',
                },
                status.includes('Completed') ? {
                    conditions: [{
                        fieldName: 'status',
                        op: 'eq',
                        value: 'Completed',
                    }],
                    op: 'and',
                } : status.includes('Canceled') ? {
                    conditions: [{
                        fieldName: 'status',
                        op: 'eq',
                        value: 'Canceled',
                    }],
                    op: 'and',
                } : {} as any],
                op: 'or',
            });
        } else if (status.includes('Completed') || status.includes('Canceled')) {
            conditions.push({
                fieldName: 'status',
                op: 'in',
                value: status,
            });
        }
        return conditions;
    }


}
